import {
  candidacySourceToLabel,
  positionCategoryToLabel,
} from "@/lib/constants";
import { CallSummary, CandidacySource, PositionCategory } from "@/types";

// Stored pay carries no unit; figures above this threshold read as annual.
const ANNUAL_PAY_THRESHOLD = 1000;

// Local so payWithUnit can use them without self-referencing the namespace.
const payIsHourly = (pay: number): boolean => pay <= ANNUAL_PAY_THRESHOLD;
const usd = (value: number): string =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const bulletListHtml = (heading: string, bullets: string[]): string =>
  `<p><strong>${heading}</strong></p><ul>${bullets
    .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
    .join("")}</ul>`;

// Local so relevantTenure can use it without self-referencing the namespace.
// Tenure is stored fractional (1.5); one decimal reads naturally, whole years drop it.
const yearsLabel = (years: number): string =>
  `${String(Number(years.toFixed(1)))} yr`;

export const formatting = {
  /**
   * Converts a structured quo call summary (bullet arrays) into rich-text
   * HTML so it renders through the same path as AI call summaries.
   */
  callSummaryHtml: (summary: CallSummary | null | undefined): string | null => {
    const summaryBullets = summary?.summary ?? [];
    const nextSteps = summary?.nextSteps ?? [];
    if (!summaryBullets.length && !nextSteps.length) return null;

    const sections: string[] = [];
    if (summaryBullets.length) {
      sections.push(bulletListHtml("Call summary", summaryBullets));
    }
    if (nextSteps.length) {
      sections.push(bulletListHtml("Next steps", nextSteps));
    }
    return sections.join("");
  },

  /**
   * Label for a candidacy source, tolerating values the backend adds before
   * the frontend enum catches up (falls back to Title Casing the raw value).
   */
  candidacySourceLabel: (source: CandidacySource): string => {
    const known = (
      candidacySourceToLabel as Partial<Record<CandidacySource, string>>
    )[source];
    if (known) return known;
    return source
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  /**
   * Whether a stored pay figure reads as an hourly rate. Pay is stored without
   * a unit, so anything at or below the annual threshold is treated as hourly.
   */
  payIsHourly,

  /**
   * Formats a stored pay figure in its own unit, as the candidate stated it
   * (e.g. "$23.50/hr", "$48,000/yr"). Hourly amounts keep cents.
   */
  payWithUnit: (pay: number): string =>
    payIsHourly(pay)
      ? `$${pay.toLocaleString(undefined, { maximumFractionDigits: 2 })}/hr`
      : `${usd(pay)}/yr`,

  currency: (value?: number | string | null): string => {
    if (value === null || value === undefined) return "—";
    const num = typeof value === "string" ? Number(value) : value;
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  },

  /**
   * Compact USD format (e.g. "$45K", "$1.2M"). `decimals` controls precision on
   * the K/M scale (defaults to 0). `roundUp` rounds up instead of nearest —
   * useful for goal denominators that shouldn't undershoot the real value.
   */
  compactUsd: (
    amount: number,
    {
      decimals = 0,
      roundUp = false,
    }: { decimals?: number; roundUp?: boolean } = {},
  ): string => {
    const round = roundUp ? Math.ceil : Math.round;
    const scale = 10 ** decimals;
    if (amount >= 1_000_000) {
      const m = round((amount / 1_000_000) * scale) / scale;
      return `$${m.toFixed(decimals)}M`;
    }
    if (amount >= 1_000) {
      const k = round((amount / 1_000) * scale) / scale;
      if (k >= 1000) {
        const m = round((amount / 1_000_000) * scale) / scale;
        return `$${m.toFixed(decimals)}M`;
      }
      return `$${k.toFixed(decimals)}K`;
    }
    return `$${round(amount).toString()}`;
  },

  fullName: (
    firstName: string | null | undefined,
    lastName: string | null | undefined,
    fallback = "Unknown",
  ): string => {
    const name = [firstName, lastName]
      .map((part) => (part ?? "").trim())
      .filter(Boolean)
      .join(" ");
    return name || fallback;
  },

  stringsDifferIgnoreCase: (a?: string | null, b?: string | null): boolean =>
    !!a && !!b && a.toLowerCase() !== b.toLowerCase(),

  initials: (
    firstName: string | null | undefined,
    lastName: string | null | undefined,
  ): string => {
    const f = (firstName ?? "").trim().charAt(0).toUpperCase();
    const l = (lastName ?? "").trim().charAt(0).toUpperCase();
    return `${f}${l}` || "?";
  },

  /**
   * Years of experience per position category ("Maintenance 3 yr · Welding
   * 1.5 yr"), largest first. Null when nothing is recorded.
   */
  years: yearsLabel,

  relevantTenure: (
    tenure: Partial<Record<PositionCategory, number>> | null | undefined,
    maxCategories = 3,
  ): string | null => {
    const entries = Object.entries(tenure ?? {})
      .filter((entry): entry is [PositionCategory, number] => {
        const [, years] = entry;
        return typeof years === "number" && years > 0;
      })
      .sort(([, a], [, b]) => b - a)
      .slice(0, maxCategories);
    if (!entries.length) return null;
    return entries
      .map(
        ([category, years]) =>
          `${positionCategoryToLabel[category]} ${yearsLabel(years)}`,
      )
      .join(" · ");
  },
};
